<template>
    <BaseDialog v-model:open="open" title="Manage Sources" show-close dialog-content-class="md:max-w-160">
        <div class="p-6 pt-0 min-w-90 md:max-w-160 w-full flex flex-col gap-4">
            <div class="flex flex-col gap-4">
                <ProfileSourcesHeader />
                <FreeTextUpload v-model:free-text="freeText" :free-text-enabled="freeTextEnabled" />

                <template v-if="documentUploadEnabled">
                    <ProfileDocumentsHeading />
                    <div v-if="isFetchingLinkedSources && linkedSources == null" class="flex flex-col gap-2">
                        <SkeletonLoader v-for="n in 2" :key="n" class="h-10 w-full rounded-lg" aria-label="Loading sources" />
                    </div>
                    <div v-else class="flex flex-col gap-2 max-h-64 overflow-y-auto">
                        <div v-if="perspectiveTitle" class="flex items-center justify-between rounded-lg border border-valence-grey-200 px-3 py-2 shrink-0">
                            <div class="flex items-center gap-2 min-w-0">
                                <ArchetypeDot :letters="perspectiveLetters" class="size-3" />
                                <div class="flex flex-wrap gap-x-4 items-baseline">
                                    <span class="body-small truncate">{{ perspectiveTitle }}</span>
                                    <span class="details text-valence-grey-750">From your perspective assessment</span>
                                </div>
                            </div>
                        </div>
                        <div v-for="row in sourcedFiles" :key="row.id" class="flex items-center justify-between rounded-lg border border-valence-grey-200 px-3 py-2 shrink-0">
                            <div class="flex items-center gap-2 min-w-0">
                                <i class="bi bi-file-text shrink-0" aria-hidden="true"></i>
                                <div class="flex flex-wrap gap-x-4 items-baseline">
                                    <span class="body-small truncate">{{ row.name }}</span>
                                    <span class="details text-valence-grey-750">
                                        <template v-if="row.last_used_at">Last used: {{ convertToDateTime(row.last_used_at).toLocaleString(DateTime.DATETIME_SHORT) }}</template>
                                        <template v-else>Not used yet in generating profile</template>
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                :aria-label="`Remove source ${row.name}`"
                                class="text-valence-grey-500 p-1 shrink-0"
                                :disabled="isDeletingSource"
                                @click="handleDelete(row.id)">
                                <i class="bi bi-x-lg" aria-hidden="true"></i>
                            </button>
                        </div>
                        <div v-if="sourcedFiles.length === 0 && !hasPerspectiveReport" class="text-center py-4">
                            <p class="body-small text-valence-grey-750">No sources yet. Upload a document below to regenerate.</p>
                        </div>
                    </div>
                    <p v-if="deleteSourceError" class="details text-red-600">{{ deleteSourceError }}</p>

                    <ProfileFileUploadArea :accepted-file-types="acceptedFileTypes" :is-uploading="isUploading" @file-upload="(files) => handleFileUpload(files, topicName)" />
                    <p v-if="fileUploadError" class="details text-red-600">{{ fileUploadError }}</p>
                </template>

                <BaseCheckbox v-model="isConfirmChecked" shape="square">
                    <p class="body-small text-valence-grey-750">This will regenerate your Collaboration Profile and overwrite any manual edits.</p>
                </BaseCheckbox>

                <div class="flex justify-between gap-2 pt-2">
                    <BaseButton theme="tertiary" :disabled="isUploading" @click="open = false">Cancel</BaseButton>
                    <BaseTooltip :disabled="canRegenerate" side="top">
                        <template #trigger>
                            <BaseButton theme="primary" :disabled="isUploading || !canRegenerate" data-testid="regenerate-button" @click="handleRegenerate">Regenerate</BaseButton>
                        </template>
                        <template #content>
                            <span class="body-small">Add at least 1 source to regenerate, or return to your profile to edit manually.</span>
                        </template>
                    </BaseTooltip>
                </div>

                <p v-if="createSourceError" class="details text-red-600">{{ createSourceError }}</p>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import type { OrganizationDetails } from "~vue/appTypes.ts";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { useProfileSources } from "~vue/composables/useProfileSources";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import { getPerspectiveProfileTitle } from "~vue/utils/perspectiveUtils";
import { DateTime } from "luxon";
import { computed, type ComputedRef, inject, ref, watch } from "vue";

import FreeTextUpload from "./FreeTextUpload.vue";
import ProfileDocumentsHeading from "./ProfileDocumentsHeading.vue";
import ProfileFileUploadArea from "./ProfileFileUploadArea.vue";
import ProfileSourcesHeader from "./ProfileSourcesHeader.vue";

type ManageSourcesDialogProps = {
    topicName?: string;
    perspectiveLetters?: string | null;
    freeTextEnabled?: boolean;
};

const organizationDetails = inject<ComputedRef<OrganizationDetails | null>>("organization_details");

const { topicName, perspectiveLetters = null, freeTextEnabled = false } = defineProps<ManageSourcesDialogProps>();

const emit = defineEmits<{ regenerate: [freeText: string] }>();

const open = defineModel<boolean>("open", { default: false });

const isConfirmChecked = ref<boolean>(false);
const documentUploadEnabled = computed(() => organizationDetails?.value?.is_document_upload_enabled);

watch(open, (val) => {
    if (!val) {
        isConfirmChecked.value = false;
    }
});

const {
    uploadedFiles,
    acceptedFileTypes,
    handleFileUpload,
    isUploading,
    fileUploadError,
    linkedSources,
    isFetchingLinkedSources,
    createSourceError,
    isDeletingSource,
    handleDeleteSource,
    deleteSourceError,
} = useProfileSources();

const sourcedFiles = computed(() => linkedSources.value?.sources.filter((s) => s.topic === topicName) ?? []);

const hasPerspectiveReport = computed(() => Boolean(perspectiveLetters));

const perspectiveTitle = computed(() => getPerspectiveProfileTitle(perspectiveLetters));

const canRegenerate = computed(
    () => isConfirmChecked.value && (!!freeText.value.trim() || sourcedFiles.value.length > 0 || uploadedFiles.value.length > 0 || hasPerspectiveReport.value),
);

const freeText = ref<string>("");

const handleRegenerate = () => {
    open.value = false;
    isConfirmChecked.value = false;
    emit("regenerate", freeText.value.trim());
};

const handleDelete = async (sourceId: string) => {
    try {
        await handleDeleteSource(sourceId);
    } catch {
        if (!deleteSourceError.value) {
            deleteSourceError.value = "An unexpected error occurred while deleting the source. Please try again.";
        }
    }
};
</script>
